import React, { useState, useCallback } from "react";
import loginImage from '../../assets/images/loginImage.svg';
import { useNavigate } from "react-router-dom";
import { Card, Button } from "@mui/material";
import { toast } from "react-toastify";
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import apiService from '../../services/actions';

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+])[A-Za-z\d!@#$%^&*()_+]{7,13}$/;

const ForgetPassword = () => {
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(location.search);
    const userCode = queryParams.get('usercode');
    console.log(userCode)
    const [formData, setFormData] = useState({
        newPass: "",
        confirmPass: ""
    });
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const passwordCriteria = useCallback((password) => {
        if (!password) return false;
        return PASSWORD_REGEX.test(password);
    }, []);

    const validateForm = useCallback(() => {
        const { newPass, confirmPass } = formData;

        if (!newPass.trim()) {
            toast.error("Please enter new password.");
            return false;
        }
        if (!passwordCriteria(newPass)) {
            toast.error("Password must contain 7–13 characters with uppercase, lowercase, number and special character.");
            return false;
        }
        if (!confirmPass.trim()) {
            toast.error("Please enter confirm Password.");
            return false;
        }
        if (newPass !== confirmPass) {
            toast.error("Password and Confirm Password do not match");
            return false;
        }
        return true;
    }, [formData, passwordCriteria]);

    const handleInputChange = useCallback((field) => (event) => {
        setFormData(prevData => ({
            ...prevData,
            [field]: event.target.value
        }));
    }, []);

    const handleUpdate = useCallback(async () => {
        if (!validateForm()) return;

        const { newPass, confirmPass: newPassword } = formData;

        const payload = {
            userCode: userCode, // Use userCode from URL
            passwordVal: btoa(newPassword),
            currentPasswordVal: btoa(newPass),
            status: 1,
            createdBy: userCode || "ADMIN",
            modifiedBy: userCode || "ADMIN"
        };

        setLoading(true);
        try {
            const response = await apiService.forgetChangePassword(payload);
            console.log(response)
            if (response?.data?.responseCode == 1) {
                toast.success(response?.data?.reponseMessage || "Password changed successfully");
                setTimeout(() => {
                    navigate('/');
                }, 2000);
            } else {
                toast.error(response?.data?.reponseMessage || "Failed to change password");
            }
        } catch {
            toast.error("Failed to change password");
        } finally {
            setLoading(false);
        }
    }, [formData, userCode, validateForm, navigate]);

    const handleCancel = useCallback(() => {
        navigate('/');
    }, [navigate]);

    return (
        <div className="flex h-screen">
            {/* Left side - Login Image */}
            <div className="flex-1 hidden lg:flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
                <div className="text-center">
                    <img src={loginImage} alt="Login" className="max-w-lg mx-auto" />
                    <h2 className="text-3xl font-bold text-gray-800 mt-8">Welcome Back!</h2>
                    <p className="text-gray-600 mt-2">Change your password to continue</p>
                </div>
            </div>

            {/* Right side - Change Password Form */}
            <div className="flex-1 flex items-center justify-center bg-white p-8">
                <Card
                    elevation={3}
                    sx={{
                        width: '480px',
                        padding: '40px',
                        borderRadius: '16px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)'
                    }}
                >
                    {/* Header */}
                    <div className="text-center mb-8">
                        <h1 className="text-2xl font-bold text-gray-800">Change Password</h1>
                        <p className="text-gray-500 mt-2">Please enter your new password below</p>
                        {userCode && (
                            <p className="text-sm text-blue-600 mt-1">User: {userCode}</p>
                        )}
                    </div>

                    {/* Form */}
                    <div className="space-y-6">
                        <div>
                            <label
                                htmlFor="newPass"
                                className="block text-sm font-medium text-gray-700 mb-2"
                            >
                                New Password
                            </label>

                            <div className="relative">
                                <input
                                    id="newPass"
                                    name="newPass"
                                    type={showNewPassword ? 'text' : 'password'}
                                    value={formData.newPass}
                                    onChange={handleInputChange("newPass")}
                                    placeholder="Enter new password"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none pr-12"
                                />

                                <button
                                    type="button"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-600 hover:text-gray-800"
                                    aria-label={showNewPassword ? "Hide password" : "Show password"}
                                >
                                    {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </button>
                            </div>
                        </div>


                        {/* Confirm Password */}
                        <div>
                            <label
                                htmlFor="confirmPass"
                                className="block text-sm font-medium text-gray-700 mb-2"
                            >
                                Confirm Password
                            </label>

                            <div className="relative">
                                <input
                                    id="confirmPass"
                                    name="confirmPass"
                                    type={showConfirmPassword ? "text" : "password"}
                                    value={formData.confirmPass}
                                    onChange={handleInputChange("confirmPass")}
                                    placeholder="Confirm new password"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none pr-12"
                                />

                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-600 hover:text-gray-800"
                                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                                >
                                    {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </button>
                            </div>
                        </div>


                        {/* Password Criteria */}
                        {formData.newPass && (
                            <div className="bg-gray-50 p-4 rounded-lg">
                                <p className="text-xs font-medium text-gray-700 mb-2">Password must contain:</p>
                                <ul className="space-y-1">
                                    <li className={`text-xs ${formData.newPass.length >= 7 && formData.newPass.length <= 13 ? 'text-green-600' : 'text-gray-500'}`}>
                                        ✓ 7-13 characters
                                    </li>
                                    <li className={`text-xs ${/[A-Z]/.test(formData.newPass) ? 'text-green-600' : 'text-gray-500'}`}>
                                        ✓ At least one uppercase letter
                                    </li>
                                    <li className={`text-xs ${/[a-z]/.test(formData.newPass) ? 'text-green-600' : 'text-gray-500'}`}>
                                        ✓ At least one lowercase letter
                                    </li>
                                    <li className={`text-xs ${/\d/.test(formData.newPass) ? 'text-green-600' : 'text-gray-500'}`}>
                                        ✓ At least one number
                                    </li>
                                    <li className={`text-xs ${/[!@#$%^&*()_+]/.test(formData.newPass) ? 'text-green-600' : 'text-gray-500'}`}>
                                        ✓ At least one special character (!@#$%^&*()_+)
                                    </li>
                                </ul>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex gap-4 pt-4">
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={handleCancel}
                                sx={{
                                    py: 1.5,
                                    borderColor: '#d1d5db',
                                    color: '#374151',
                                    '&:hover': {
                                        borderColor: '#9ca3af',
                                        bgcolor: '#f9fafb'
                                    },
                                    textTransform: 'none',
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    borderRadius: '8px'
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={handleUpdate}
                                disabled={loading}
                                sx={{
                                    py: 1.5,
                                    bgcolor: '#2563eb',
                                    '&:hover': { bgcolor: '#1d4ed8' },
                                    '&:disabled': { bgcolor: '#93c5fd' },
                                    textTransform: 'none',
                                    fontSize: '16px',
                                    fontWeight: 500,
                                    borderRadius: '8px'
                                }}
                            >
                                {loading ? 'Updating...' : 'Change Password'}
                            </Button>
                        </div>
                    </div>

                    <div className="mt-8 text-center">
                        <p className="text-sm text-gray-500">
                            Secure password change • Make sure to remember your new password
                        </p>
                    </div>
                </Card>
            </div>
        </div>
    );
};

export default ForgetPassword;